import React, { Children, createRef } from 'react';
import { getContext, useCombinedRefs } from 'common';
import { ButtonText, ButtonTextProps } from 'components/Button';
import Styled from './styles';

const BUTTON_CONFIG = {
  role: 'button',
};

export interface ButtonCoreProps extends React.ComponentPropsWithRef<'button'> {
  /** The styled-component wrapper, this needs to be made considering the content of the ButtonCore. */
  Wrapper: any;
  /** The content of the button, text and Icon components are supported. */
  children: React.ReactNode;
  /** If `true`, the button will be disabled. */
  disabled?: boolean;
  /** If `true`, the visuals will be according to IconButton */
  iconButton?: boolean;
  /** onClick handler */
  onClick?: React.MouseEventHandler;
  /** To configure text wrapper element. It is used when only a string is passed as children. */
  buttonTextProps?: Partial<ButtonTextProps>;
  /** The theme of the Button */
  $theme?: Record<string, unknown>;
  /** Expands the button to fit the width of it's container. */
  fullWidth?: boolean;
  /** The variant of the button. "secondary" is identical with "default", please use "secondary" instead of "default" as "default" will be deprecated soon. */
  variant?: 'secondary' | 'default' | 'call-to-action' | 'outlined';
  /** To override the theme. */
  theme?: any;
  /** $size, only used by IconButton */
  $size?: 'small' | 'medium';
  /** @ignore toggles isPressed styles */
  $isPressed?: boolean;
}

/** Any other props than `children` will be provided to the root `button` element. */
const ButtonCore = React.forwardRef<HTMLElement, ButtonCoreProps>((props: ButtonCoreProps, ref) => getContext(({ RTL }) => {
  const buttonRef = createRef<HTMLElement>();
  const combinedRef = useCombinedRefs(ref, buttonRef);
  const {
    children,
    disabled,
    Wrapper,
    iconButton,
    onClick,
    theme,
    $theme,
    fullWidth,
    buttonTextProps,
    variant,
    ...otherProps
  } = props;

  const buttonProps = {
    ...BUTTON_CONFIG,
    'aria-disabled': disabled,
    ...otherProps,
  };

  const getChildren = () => {
    const child = Children.count(children) === 1
    && typeof Children.toArray(children)[0] === 'string'
    && <ButtonText {...buttonTextProps}>{Children.toArray(children)[0] as string}</ButtonText>;
    return child || children;
  };

  const handleClick = (eventData) => {
    buttonRef && buttonRef.current && buttonRef.current.focus(); // To force FF to focus on the Button after clicking.
    onClick && onClick(eventData);
  };
  return (
    <Wrapper
      $rtl={RTL}
      disabled={disabled}
      ref={combinedRef}
      onClick={handleClick}
      $theme={theme || $theme}
      $fullWidth={fullWidth}
      variant={variant}
      {...buttonProps}
    >
      <Styled.ContentWrapper
        iconButton={iconButton}
        RTL={RTL}
        $fullWidth={fullWidth}
      >
        {getChildren()}
      </Styled.ContentWrapper>
    </Wrapper>
  );
}));

ButtonCore.displayName = 'ButtonCore';

ButtonCore.defaultProps = {
  disabled: false,
  iconButton: false,
  onClick: undefined,
  buttonTextProps: {},
  fullWidth: false,
  variant: undefined,
  $size: 'medium',
  theme: {},
  $theme: {},
  $isPressed: false,
};

export default ButtonCore;
